import { ClockIcon, TextField, TextFieldProps } from '@eventbrite/marmalade';
import * as Popover from '@radix-ui/react-popover';
import React, { useCallback, useEffect, useState } from 'react';
import { useMedia } from 'react-use';
import { useDefaultLocale } from '../../hooks/useDefaultLocale';
import styles from './TimeInput.module.scss';
import { Timepicker } from './Timepicker';
import type { TimeConfig } from './useTime';
export interface TimeInputProps extends TextFieldProps, TimeConfig {
    onChange?: (t: string) => void;
}

export enum TimeInputTestIds {
    Input = 'TimeInputInput',
    Picker = 'TimeInputPicker',
}

const focusTrap = (e: Event) => e.preventDefault();

const formatDisplayValue = ({
    value,
    locale,
    native,
}: {
    value: string;
    locale?: string;
    native: boolean;
}) => {
    if (!value) {
        return '';
    }

    if (native) {
        return value;
    }

    return new Date(`${new Date().toDateString()} ${value}`).toLocaleTimeString(
        locale || [],
        {
            timeStyle: 'short',
        },
    );
};

const formatTimeValue = ({
    value,
    locale,
}: {
    value: string;
    locale: string;
}) => {
    return new Date(`2023-09-01 ${value}`).toLocaleTimeString(locale || [], {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
    });
};

const roundToTime = ({
    value,
    interval,
    locale,
}: {
    value: string;
    interval: number;
    locale: string;
}) => {
    const slot = interval * 60 * 1000;
    const date = new Date(`2023-09-01 ${value}`).getTime();
    const roundedDate = new Date(Math.round(date / slot) * slot);

    return new Date(roundedDate).toLocaleTimeString(locale || [], {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
    });
};

const parseTimeValue = ({
    value,
    interval,
    locale,
}: {
    value: string;
    interval: number;
    locale: string;
}) => {
    const [hour = 0, minutes = 0] = value.split(':');
    const time = formatTimeValue({ value: `${hour}:${minutes}`, locale });

    if (time === 'Invalid Date') {
        throw new Error(`Invalid time value ${value}`);
    }

    return roundToTime({ value: time, interval, locale });
};

const NAVIGATION_KEYS = ['ArrowDown', 'ArrowUp', 'Enter'];

export const TimeInput = React.forwardRef<HTMLInputElement, TimeInputProps>(
    (props: TimeInputProps, ref) => {
        const {
            interval = 30,
            locale,
            minTime,
            maxTime,
            onChange,
            ...textFieldProps
        } = props;

        const browserLocale = useDefaultLocale(locale);
        const { onBlur, value } = textFieldProps;
        const popoverTriggerContainerRef = React.useRef<HTMLDivElement>(null);
        const [editing, setEditing] = useState(false);
        const [open, setOpen] = useState(false);
        const isTouchDevice = useMedia('(hover: none) and (pointer: coarse)');
        const [fieldValue, updateFieldValue] = useState(
            formatDisplayValue({
                value: value as string,
                locale: browserLocale,
                native: isTouchDevice,
            }),
        );

        const onKeyDownHandler = useCallback(
            (event: React.KeyboardEvent<HTMLInputElement>) => {
                if (NAVIGATION_KEYS.includes(event.key)) {
                    return;
                }

                setEditing(true);
                setOpen(true);
            },
            [],
        );

        const onFocusHandler = useCallback(() => {
            setOpen(true);
        }, []);

        const syncOnChange = useCallback(
            (time: string) => {
                if (time !== value) {
                    onChange?.(time);
                }

                updateFieldValue(
                    formatDisplayValue({
                        value: time,
                        locale: browserLocale,
                        native: isTouchDevice,
                    }),
                );
            },
            [onChange, browserLocale, isTouchDevice, value],
        );

        const onBlurHandler = useCallback(() => {
            if (editing || isTouchDevice) {
                try {
                    const time = parseTimeValue({
                        value: fieldValue,
                        interval,
                        locale: browserLocale,
                    });
                    syncOnChange(time);
                } catch (e) {
                    updateFieldValue(
                        formatDisplayValue({
                            value: value as string,
                            locale: browserLocale,
                            native: isTouchDevice,
                        }),
                    );
                } finally {
                    setEditing(false);
                    setOpen(false);
                }

                onBlur?.({} as React.FocusEvent<HTMLInputElement>);
            }
        }, [
            onBlur,
            browserLocale,
            value,
            editing,
            fieldValue,
            interval,
            isTouchDevice,
            syncOnChange,
        ]);

        const onCloseHandler = useCallback(() => {
            setOpen(false);
            onBlur?.({} as React.FocusEvent<HTMLInputElement>);
        }, [onBlur]);

        const onTimeChange = useCallback(
            (time: string) => {
                syncOnChange(time);
                setOpen(false);
                setEditing(false);
            },
            [syncOnChange],
        );

        const onFieldChange = useCallback((value: string) => {
            updateFieldValue(value);
        }, []);

        useEffect(() => {
            updateFieldValue(
                formatDisplayValue({
                    value: value as string,
                    locale: browserLocale,
                    native: isTouchDevice,
                }),
            );
        }, [browserLocale, value, isTouchDevice]);

        return (
            <div ref={popoverTriggerContainerRef}>
                {isTouchDevice ? (
                    <TextField
                        {...textFieldProps}
                        data-testid={TimeInputTestIds.Input}
                        onChange={onFieldChange}
                        value={fieldValue}
                        autoComplete="off"
                        onBlur={onBlurHandler}
                        onKeyDown={onKeyDownHandler}
                        type="time"
                        ref={ref}
                    />
                ) : (
                    <Popover.Root open={open}>
                        <Popover.Trigger asChild>
                            <TextField
                                {...textFieldProps}
                                data-testid={TimeInputTestIds.Input}
                                role="combobox"
                                onChange={onFieldChange}
                                value={fieldValue}
                                autoComplete="off"
                                onBlur={onBlurHandler}
                                onFocus={onFocusHandler}
                                onKeyDown={onKeyDownHandler}
                                type="text"
                                prefix={<ClockIcon size="small" />}
                                ref={ref}
                            />
                        </Popover.Trigger>
                        <Popover.Portal>
                            <Popover.Content
                                data-testid={TimeInputTestIds.Picker}
                                onOpenAutoFocus={focusTrap}
                                onPointerDownOutside={onCloseHandler}
                                className={styles.content}
                                style={{
                                    width: popoverTriggerContainerRef.current
                                        ?.clientWidth,
                                }}
                            >
                                <Timepicker
                                    interval={interval}
                                    locale={browserLocale}
                                    minTime={minTime}
                                    maxTime={maxTime}
                                    onTimeChange={onTimeChange}
                                    selectedTime={value as string}
                                    filter={
                                        editing
                                            ? (fieldValue as string)
                                            : undefined
                                    }
                                />
                            </Popover.Content>
                        </Popover.Portal>
                    </Popover.Root>
                )}
            </div>
        );
    },
);
